import {
  assignIfExists,
  assignProps,
  assignValue,
  findMatch,
  findSelected,
  firstSelected, removeItemByID,
} from '@/utils/resultsHelper';
import { responseError, pruneFieldErrors } from '@/utils/errors';
import { defaultLifeCycle, saveStates, states } from '@/constants/lifecycle';
import withDraft from '@/utils/draftHelper';
import {
  activeCondition,
  activeConditions,
  conditionsToDelete,
  findDatasetConditions,
  mapItemsToDatasetConditions,
  findDatasetCondition,
  newDatasetCondition,
} from './state';
import actions from '../constants';

const sortByConditionCode = (a, b) => a.condition.code.localeCompare(b.condition.code);

const upsertDatasetCondition = withDraft((draft) => {
  draft.activeCondition.saveStatus = states.PENDING;
  draft.activeCondition.saveErrors = [];
});

const hideDatasetCondition = withDraft((draft) => { draft.activeCondition = activeCondition(); });

const hideDatasetConditions = withDraft((draft) => { draft.activeConditions = activeConditions(); });

const showDatasetConditions = withDraft((draft, action) => {
  assignIfExists(draft.activeConditions, { ...action, open: true, readStatus: states.PENDING });
});

const hideRemoveDatasetCondition = withDraft((draft) => {
  draft.datasetConditions.deletion = conditionsToDelete();
});

const updateActiveDatasetConditionContext = withDraft((draft, action) => {
  const selectedCondition = firstSelected(draft.activeCondition.items);
  assignProps(selectedCondition.context, action.properties);
  draft.activeCondition.saveErrors = pruneFieldErrors(draft.activeCondition.saveErrors, action.field);
  draft.activeCondition.isDirty = true;
});

const updateActiveDatasetConditionConfiguration = withDraft((draft, action) => {
  const selectedCondition = firstSelected(draft.activeCondition.items);
  assignProps(selectedCondition.configuration, action.configuration);
  draft.activeCondition.isDirty = true;
});

const updateActiveDatasetConditionProperties = withDraft((draft, action) => {
  assignIfExists(draft.activeCondition, action.properties, defaultLifeCycle, true);
});

const updateActiveDatasetCondition = withDraft((draft, action) => {
  findSelected(draft.activeCondition.items).forEach((fields) => assignValue(fields, 'selected', false));
  const fields = findMatch(draft.activeCondition.items, action.condition.code, 'condition.code');

  if (!fields) {
    draft.activeCondition.items.push(newDatasetCondition(action.condition.code, { condition: action.condition }));
  } else {
    fields.selected = true;
  }

  draft.activeCondition.saveStatus = states.NONE;
  draft.activeCondition.saveErrors = [];
  draft.activeCondition.isDirty = true;
});

const upsertDatasetConditionError = withDraft((draft, action) => {
  draft.activeCondition.saveStatus = states.ERROR;
  draft.activeCondition.saveErrors = [responseError(action)];
});

const showDatasetConditionsError = withDraft((draft, action) => {
  draft.activeConditions.readStatus = states.ERROR;
  draft.activeConditions.readErrors = [responseError(action)];
});

const showDatasetConditionsSuccess = withDraft((draft, action) => {
  assignProps(draft.activeConditions, {
    items: action.results,
    readStatus: states.SUCCESS,
    readErrors: [],
  });
});

const fetchDatasetConditions = withDraft((draft, action) => {
  const item = findDatasetConditions(draft, action);
  if (item) {
    item.readStatus = states.PENDING;
    item.readErrors = [];
  } else {
    draft.datasetConditions[action.category].results.push({
      code: action.code,
      readStatus: states.PENDING,
      readErrors: [],
    });
  }
});

const fetchDatasetConditionsSuccess = withDraft((draft, action) => {
  const item = findDatasetConditions(draft, action);
  assignProps(item, {
    conditions: action.results,
    readStatus: states.SUCCESS,
    readErrors: [],
    ...saveStates,
  });
});

const fetchDatasetConditionsError = withDraft((draft, action) => {
  const item = findDatasetConditions(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const addDatasetConditionSuccess = withDraft((draft, action) => {
  const item = findDatasetConditions(draft, action);
  draft.activeCondition = activeCondition();
  item.saveStatus = states.SUCCESS;
  item.saveErrors = [];
  item.conditions.push(action.result);
  item.conditions.sort(sortByConditionCode);
});

const updateDatasetConditionSuccess = withDraft((draft, action) => {
  const item = findDatasetConditions(draft, action);
  const index = item.conditions.findIndex((c) => c.id === action.result.id);
  item.conditions[index] = action.result;
  item.saveStatus = states.SUCCESS;
  item.saveErrors = [];
  draft.activeCondition = activeCondition();
});

const showRemoveDatasetCondition = withDraft((draft, action) => {
  const { condition } = findDatasetCondition(draft, action);
  draft.datasetConditions.deletion = conditionsToDelete(
    true,
    action.name,
    [{ ...condition, code: action.code }],
  );
});

const showEditDatasetCondition = withDraft((draft, action) => {
  const { condition } = findDatasetCondition(draft, action);
  draft.activeCondition.category = action.category;
  draft.activeCondition.code = action.code;
  draft.activeCondition.name = action.name;
  draft.activeCondition.open = true;
  draft.activeCondition.items.push({
    id: condition.id,
    ...newDatasetCondition(condition.condition.code, condition),
  });
});

const showAddDatasetCondition = withDraft((draft, action) => {
  draft.activeCondition.category = action.category;
  draft.activeCondition.code = action.code;
  draft.activeCondition.isNew = true;
  draft.activeCondition.open = true;
  draft.activeCondition.name = action.name;
});

const toggleDatasetConditionAdvisory = withDraft((draft, action) => {
  const { condition } = findDatasetCondition(draft, action);
  if (condition) {
    condition.saveStatus = states.PENDING;
    condition.saveErrors = [];
  }
});

const toggleDatasetConditionAdvisoryError = withDraft((draft, action) => {
  const { condition } = findDatasetCondition(draft, action);
  if (condition) {
    condition.saveStatus = states.ERROR;
    condition.saveErrors = [responseError(action)];
  }
});

const removeDatasetCondition = withDraft((draft, action) => {
  mapItemsToDatasetConditions(draft, action).conditions.forEach(({ condition }) => {
    assignProps(condition, { destroyStatus: states.PENDING, destroyErrors: [] });
  });
});

const removeDatasetConditionSuccess = withDraft((draft, action) => {
  draft.datasetConditions.deletion = conditionsToDelete();
  mapItemsToDatasetConditions(draft, action).conditions.forEach(({ parent, item }) => {
    assignProps(parent, {
      conditions: parent.conditions.filter((r) => r.id !== item.id),
    });
  });
});

const removeDatasetConditionError = withDraft((draft, action) => {
  draft.datasetConditions.deletion = conditionsToDelete();
  mapItemsToDatasetConditions(draft, action).conditions.forEach(({ condition, item }) => {
    assignProps(condition, { destroyStatus: states.ERROR, destroyErrors: [item.error] });
  });
});

const downloadDatasetConditionAttachmentComplete = withDraft((draft, { datasetCondition: { id } }) => {
  draft.attachments.downloadsInProgress.push({ id });
  draft.attachments.downloadsInProgress = removeItemByID(draft.attachments.downloadsInProgress, { id });
});

const downloadDatasetConditionAttachment = withDraft((draft, { datasetCondition: { id } }) => {
  draft.attachments.downloadsInProgress.push({ id });
});

export default {
  [actions.ADD_DATASET_CONDITION]: upsertDatasetCondition,
  [actions.ADD_DATASET_CONDITION_ERROR]: upsertDatasetConditionError,
  [actions.ADD_DATASET_CONDITION_SUCCESS]: addDatasetConditionSuccess,
  [actions.DOWNLOAD_DATASET_CONDITION_ATTACHMENT]: downloadDatasetConditionAttachment,
  [actions.DOWNLOAD_DATASET_CONDITION_ATTACHMENT_ERROR]: downloadDatasetConditionAttachmentComplete,
  [actions.DOWNLOAD_DATASET_CONDITION_ATTACHMENT_SUCCESS]: downloadDatasetConditionAttachmentComplete,
  [actions.FETCH_DATASET_CONDITIONS]: fetchDatasetConditions,
  [actions.FETCH_DATASET_CONDITIONS_ERROR]: fetchDatasetConditionsError,
  [actions.FETCH_DATASET_CONDITIONS_SUCCESS]: fetchDatasetConditionsSuccess,
  [actions.HIDE_DATASET_CONDITION]: hideDatasetCondition,
  [actions.HIDE_DATASET_CONDITIONS]: hideDatasetConditions,
  [actions.HIDE_REMOVE_DATASET_CONDITION]: hideRemoveDatasetCondition,
  [actions.REMOVE_DATASET_CONDITION]: removeDatasetCondition,
  [actions.REMOVE_DATASET_CONDITION_ERROR]: removeDatasetConditionError,
  [actions.REMOVE_DATASET_CONDITION_SUCCESS]: removeDatasetConditionSuccess,
  [actions.SHOW_ADD_DATASET_CONDITION]: showAddDatasetCondition,
  [actions.SHOW_DATASET_CONDITIONS]: showDatasetConditions,
  [actions.SHOW_DATASET_CONDITIONS_ERROR]: showDatasetConditionsError,
  [actions.SHOW_DATASET_CONDITIONS_SUCCESS]: showDatasetConditionsSuccess,
  [actions.SHOW_EDIT_DATASET_CONDITION]: showEditDatasetCondition,
  [actions.SHOW_REMOVE_DATASET_CONDITION]: showRemoveDatasetCondition,
  [actions.TOGGLE_DATASET_CONDITION_ADVISORY]: toggleDatasetConditionAdvisory,
  [actions.TOGGLE_DATASET_CONDITION_ADVISORY_ERROR]: toggleDatasetConditionAdvisoryError,
  [actions.UPDATE_ACTIVE_DATASET_CONDITION]: updateActiveDatasetCondition,
  [actions.UPDATE_ACTIVE_DATASET_CONDITION_CONFIGURATION]: updateActiveDatasetConditionConfiguration,
  [actions.UPDATE_ACTIVE_DATASET_CONDITION_CONTEXT]: updateActiveDatasetConditionContext,
  [actions.UPDATE_ACTIVE_DATASET_CONDITION_PROPERTIES]: updateActiveDatasetConditionProperties,
  [actions.UPDATE_DATASET_CONDITION]: upsertDatasetCondition,
  [actions.UPDATE_DATASET_CONDITION_ERROR]: upsertDatasetConditionError,
  [actions.UPDATE_DATASET_CONDITION_SUCCESS]: updateDatasetConditionSuccess,
};
